import { ref, defineComponent } from 'vue';
import { of } from 'rxjs';
import { tap, map, switchMap, filter } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import cloneDeep from "lodash.clonedeep";
export default defineComponent({
    name: 'password-input',
    props: {
        readOnly: {
            type: Boolean,
            default: false
        },
        name: {
            type: String,
            default: ''
        },
        email: {
            type: String,
            default: ''
        },
        hint: Boolean,
        removeNameRequirement: Boolean,
        disable: Boolean
    },
    emits: ['password', 'focus'],
    setup(props, context) {
        // if input type is password (password visible / hidden)
        const isPwd = ref(true);
        /******
         * Check password
         ******/
        const password = ref('');
        const { subject: passwordCheck$ } = rxInitSubjectCallback();
        const checkPwd = useObservable(passwordCheck$.pipe(tap(v => {
            password.value = v;
        }), filter(v => v !== null), map(v => cloneDeep(v)), switchMap(pwd => {
            return of(pwd).pipe(map(v => {
                const lowerCaseLetters = /[a-z]/g;
                const matchLower = v.match(lowerCaseLetters);
                return {
                    pwd: v,
                    matchLower: matchLower,
                    matchUpper: null,
                    matchNumber: null,
                    matchSpecialChar: null,
                    matchLength: 0,
                    noMatchName: false,
                    strength: null,
                    color: null,
                    score: 0,
                    pass: false
                };
            }), map(v => {
                const passwordChk = v.pwd;
                const upperCaseLetters = /[A-Z]/g;
                const matchUpper = passwordChk.match(upperCaseLetters);
                v.matchUpper = matchUpper;
                return v;
            }), map(v => {
                const passwordChk = v.pwd;
                const numbers = /[0-9]/g;
                const matchNumber = passwordChk.match(numbers);
                v.matchNumber = matchNumber;
                return v;
            }), map(v => {
                const passwordChk = v.pwd;
                const specChar = /[^a-zA-Z\d]+/g;
                const matchSpecialChar = passwordChk.match(specChar);
                v.matchSpecialChar = matchSpecialChar;
                return v;
            }), map(v => {
                const passwordChk = v.pwd;
                v.matchLength = passwordChk.length;
                return v;
            }), map(v => {
                const passwordChk = v.pwd;
                v.noMatchName = (passwordChk.toLowerCase().indexOf(props.name.toLowerCase()) === -1) && (props.email.toLowerCase().indexOf(passwordChk.toLowerCase()) === -1);
                return v;
            }));
        }), map((v) => {
            /*****
             * Calculate score of password
             ******/
            let score = 0;
            if (v.matchLength < 4 && v.matchLength !== 0) {
                score += 5;
            }
            else if (v.matchLength > 4 && v.matchLength < 8) {
                score += 10;
            }
            else if (v.matchLength >= 8 && v.matchLength < 12) {
                score += 20;
            }
            else if (v.matchLength >= 12) {
                score += 35;
            }
            if (v.matchLower && v.matchUpper) {
                score += 20;
            }
            else if (v.matchLower) {
                score += 10;
            }
            if (v.matchNumber) {
                if (v.matchNumber.length > 2) {
                    score += 20;
                }
                else {
                    score += 10;
                }
            }
            if (v.matchSpecialChar) {
                if (v.matchSpecialChar.length > 2) {
                    score += 20;
                }
                else {
                    score += 10;
                }
            }
            if (v.matchNumber && v.matchLower && v.matchSpecialChar && v.matchUpper) {
                score += 5;
            }
            else if (v.matchNumber && (v.matchLower || v.matchUpper) && v.matchSpecialChar) {
                score += 3;
            }
            else if (v.matchNumber && (v.matchLower || v.matchUpper)) {
                score += 2;
            }
            if (score < 43) {
                v.strength = 'Weak';
                v.color = 'red';
            }
            else if (score < 90) {
                v.strength = 'Good';
                v.color = 'positive';
            }
            else if (score < 100) {
                v.strength = 'Strong';
                v.color = 'teal';
            }
            else if (score === 100) {
                v.strength = 'Very Strong';
                v.color = 'green';
            }
            v.score = score;
            if ((score >= 43) &&
                (props.removeNameRequirement || v.noMatchName) &&
                (v.matchNumber || v.matchSpecialChar)) {
                v.pass = true;
            }
            else {
                v.pass = false;
            }
            context.emit('password', v);
            return v;
        })));
        /**
         * Clear password input
         */
        function clearInput() {
            password.value = '';
        }
        const showStrong = ref(false);
        return {
            isPwd,
            password,
            checkPwd,
            passwordCheck$,
            clearInput,
            showStrong,
        };
    }
});
